import {
  createCompany,
  deleteCompany,
  getCompanies,
  getCompanyById,
  updateCompany,
} from "@/services";
import type { UpdateCompanyData } from "@/types/company.type";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Hook pour récupérer la liste des entreprises avec pagination, recherche et filtre par statut
export const useCompanies = (
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    companyId?: string;
  },
  options?: {
    enabled?: boolean; // si true, la requête est exécutée, si false, la requête n'est pas exécutée
  }
) => {
  return useQuery({
    queryKey: ["companies", params],
    queryFn: () => getCompanies(params),
    enabled: options?.enabled ?? true, // si true, la requête est exécutée, si false, la requête n'est pas exécutée
  });
};

// Hook pour récupérer une entreprise par son id
export const useCompany = (
  id: string,
  options?: {
    enabled?: boolean; // si true, la requête est exécutée, si false, la requête n'est pas exécutée
  }
) => {
  return useQuery({
    queryKey: ["company", id],
    queryFn: () => getCompanyById(id),
    enabled: options?.enabled ?? !!id, // Ne fait la requête que si id existe
  });
};

// Hook pour créer une entreprise
export const useCreateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCompany, //createCompany service function pour créer une entreprise

    // si la création est réussie, on invalide le cache des entreprises
    onSuccess: () => {
      // Invalider le cache des entreprises
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

// Hook pour modifier une entreprise
export const useUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyData }) =>
      updateCompany(id, data),
    // onSuccess: (updatedCompany, { id }) => {
    //   // Mise à jour optimiste du cache
    //   queryClient.setQueryData(["companies"], (old: any) => {
    //     if (!old) return old;
    //     return {
    //       ...old,
    //       companies: old.companies.map((company: Company) =>
    //         company.id === id ? { ...company, ...updatedCompany } : company
    //       ),
    //     };
    //   });
    // },
    onSuccess: (_, { id }) => {
      // id est l'id de l'entreprise qui a été mise à jour
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["company", id] });
    },
  });
};

// Hook pour supprimer une entreprise
export const useDeleteCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};
